import React, { useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import { useDispatch } from "react-redux";
import DOMPurify from "dompurify";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import {
  MessageSquare,
  Mail,
  MoreVertical,
  PanelRightOpen,
  Info,
  ArrowLeft,
  Trash2,
  Sparkles,
  EyeOff,
} from "lucide-react";

import {
  changeAISuggestion,
  changeDisplayNotes,
  deleteChat,
  sendMessage,
  sendMessageWithoutConversation,
} from "slices/chat/thunk";
import SplitButton from "Common/Components/SplitButton";
import DeleteModal from "Common/DeleteModal";
import avatar from "assets/images/users/user-2.jpg";
import "./index.scss";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { cn } from "lib/utils";

import ContactInfoPanel from "./ContactInfoPanel";
import { SendType } from "./types";

interface ChatMessagesProps {
  chatDetail: any;
  contactDetail: any;
  loadingChatDetail: boolean;
  onChatDeleted?: () => void;
  onBackToChatList?: () => void;
}

const initials = (name?: string) =>
  (name ?? "User")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

const formatDateTime = (utcTimeString: string): string => {
  if (!utcTimeString) return "";
  try {
    let date: Date;
    if (
      utcTimeString.includes("+") ||
      utcTimeString.includes("Z") ||
      utcTimeString.endsWith("z")
    ) {
      date = new Date(utcTimeString);
    } else {
      date = new Date(utcTimeString + "Z");
    }
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    return "";
  }
};

const bubbleStyle = (item: any) => {
  if (item?.isClientMessage) {
    return "bg-muted text-foreground";
  }
  switch (item?.sendType) {
    case SendType.Email:
    case SendType.EmailMarketing:
    case SendType.RequestReview:
      return "bg-primary/10 text-foreground";
    case SendType.ActivityNote:
    case SendType.SystemNote:
      return "bg-warning/15 text-foreground";
    default:
      return "bg-success/15 text-foreground";
  }
};

const sendTypeLabel = (sendType: SendType): React.ReactNode => {
  switch (sendType) {
    case SendType.SMS:
      return <MessageSquare className="h-3 w-3" />;
    case SendType.Email:
      return <Mail className="h-3 w-3" />;
    case SendType.EmailMarketing:
      return <span className="text-[9px] font-medium">Email Marketing</span>;
    case SendType.SmsMarketing:
      return <span className="text-[9px] font-medium">SMS Marketing</span>;
    case SendType.RequestReview:
      return <span className="text-[9px] font-medium">Request Review</span>;
    case SendType.ActivityNote:
      return <span className="text-[9px] font-medium">Activity Note</span>;
    case SendType.SystemNote:
      return <span className="text-[9px] font-medium">System Note</span>;
    default:
      return null;
  }
};

const ChatMessages: React.FC<ChatMessagesProps> = ({
  chatDetail,
  contactDetail,
  loadingChatDetail,
  onChatDeleted,
  onBackToChatList,
}) => {
  const dispatch = useDispatch<any>();
  const [conversationId, setConversationId] = useState<string>("");
  const [contactId, setContactId] = useState<string>("");
  const [sendType, setSendType] = useState<SendType>(SendType.SMS);
  const [buttonText, setButtonText] = useState<SendType>(SendType.SMS);
  const [curMessage, setCurMessage] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [emailSubject, setEmailSubject] = useState();
  const [showContactPanel, setShowContactPanel] = useState<boolean>(false);
  const [aiSuggestionsEnabled, setAiSuggestionsEnabled] = useState<boolean>(true);
  const [isHideNotes, setIsHideNotes] = useState<boolean>(false);
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);

  const chatRef = useRef<any>(null);

  useEffect(() => {
    if (chatDetail && chatDetail.id) {
      setConversationId(chatDetail.id);
      setContactId(chatDetail.contactId);
      if (chatDetail.messages?.length > 0) {
        const lastMessage = chatDetail.messages[chatDetail.messages.length - 1];
        setSendType(
          lastMessage?.sendType === SendType.SystemNote
            ? SendType.ActivityNote
            : lastMessage?.sendType || SendType.ActivityNote
        );
        setButtonText(
          lastMessage?.sendType === SendType.SystemNote
            ? SendType.ActivityNote
            : lastMessage?.sendType || SendType.ActivityNote
        );
        setEmailSubject(lastMessage?.emailSubject);
      }
    } else {
      setConversationId("");
      setContactId("");
    }
  }, [chatDetail]);

  useEffect(() => {
    setIsHideNotes(chatDetail?.isHideNotes || false);
    setAiSuggestionsEnabled(chatDetail?.enableSuggestion || false);
  }, [chatDetail?.isHideNotes, chatDetail?.enableSuggestion]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (chatRef.current?.getScrollElement) {
        const scrollElement = chatRef.current.getScrollElement();
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    };
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [chatDetail?.id, chatDetail?.messages]);

  useEffect(() => {
    if (contactDetail?.id) {
      const hasEmail =
        contactDetail?.email != null && contactDetail.email.trim() !== "";
      const hasPhoneNumber =
        contactDetail?.phoneNumber != null &&
        contactDetail.phoneNumber.trim() !== "";

      if (hasEmail) setSendType(SendType.Email);
      else if (hasPhoneNumber) setSendType(SendType.SMS);
      else setSendType(SendType.ActivityNote);
    }
  }, [contactDetail]);

  useEffect(() => {
    setButtonText(sendType);
  }, [sendType]);

  const addMessage = async () => {
    if (curMessage !== "") {
      setCurMessage("");
      setIsSendingMessage(true);
      if (conversationId) {
        const message = {
          conversationId,
          provider: "User system",
          content: curMessage,
          sendType,
          contactId: contactId,
          emailSubject: sendType === SendType.Email ? emailSubject : undefined,
        };
        await dispatch(sendMessage(message));
      } else {
        const message = {
          contactId: contactId !== "" || contactDetail?.id,
          provider: "User system",
          content: curMessage,
          sendType,
        };
        await dispatch(sendMessageWithoutConversation(message));
      }
      setIsSendingMessage(false);
    }
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addMessage();
    }
  };

  const handleAiToggle = async () => {
    await dispatch(
      changeAISuggestion({
        id: conversationId,
        payload: !aiSuggestionsEnabled,
      })
    );
    setAiSuggestionsEnabled(!aiSuggestionsEnabled);
  };

  const handleHideNotesToggle = async () => {
    await dispatch(
      changeDisplayNotes({ id: conversationId, payload: !isHideNotes })
    );
    setIsHideNotes(!isHideNotes);
  };

  const confirmDelete = () => {
    if (conversationId) {
      dispatch(deleteChat(conversationId));
      if (onChatDeleted) onChatDeleted();
    }
    setShowDeleteModal(false);
  };

  const displayedMessages = (chatDetail.messages || []).filter(
    (item: any) => !isHideNotes || item.sendType !== SendType.SystemNote
  );

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBackToChatList}
          aria-label="Back to chat list"
          className="xl:hidden"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md p-1 text-left transition-colors hover:bg-muted/60"
          onClick={() => setShowContactPanel(true)}
        >
          <Avatar className="h-10 w-10">
            {chatDetail?.image ? (
              <AvatarImage src={avatar} alt={chatDetail?.name} />
            ) : null}
            <AvatarFallback>
              {initials(chatDetail?.name ?? contactDetail?.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-foreground sm:text-base">
              {chatDetail?.name ?? contactDetail?.name ?? "Unknown contact"}
            </h3>
            {contactDetail?.email ? (
              <p className="truncate text-xs text-muted-foreground">
                {contactDetail.email}
              </p>
            ) : null}
          </div>
        </button>

        {conversationId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Conversation actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleAiToggle();
                }}
                className="cursor-pointer"
              >
                <Sparkles />
                <span className="flex-1">AI response</span>
                <span
                  className={cn(
                    "h-4 w-7 rounded-full transition-colors",
                    aiSuggestionsEnabled ? "bg-primary" : "bg-muted-foreground/30"
                  )}
                >
                  <span
                    className={cn(
                      "block h-3 w-3 translate-y-0.5 rounded-full bg-card transition-transform",
                      aiSuggestionsEnabled ? "translate-x-3.5" : "translate-x-0.5"
                    )}
                  />
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleHideNotesToggle();
                }}
                className="cursor-pointer"
              >
                <EyeOff />
                <span className="flex-1">Hide notes</span>
                <span
                  className={cn(
                    "h-4 w-7 rounded-full transition-colors",
                    isHideNotes ? "bg-primary" : "bg-muted-foreground/30"
                  )}
                >
                  <span
                    className={cn(
                      "block h-3 w-3 translate-y-0.5 rounded-full bg-card transition-transform",
                      isHideNotes ? "translate-x-3.5" : "translate-x-0.5"
                    )}
                  />
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteModal(true)}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <Trash2 />
                Delete conversation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowContactPanel(!showContactPanel)}
          aria-label="Toggle contact panel"
        >
          <PanelRightOpen className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages */}
      <div className="relative flex-1 bg-gradient-to-b from-muted/20 to-transparent">
        {loadingChatDetail ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary [animation-delay:150ms]" />
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary [animation-delay:300ms]" />
            </div>
          </div>
        ) : (
          <SimpleBar
            ref={chatRef}
            className="h-[calc(100vh-15rem)] px-3 xl:px-6"
          >
            <ul className="flex flex-col gap-8 py-4">
              {displayedMessages.map((item: any, key: number) => {
                const isClient = item.isClientMessage;
                const senderInitials = isClient
                  ? initials(chatDetail?.name)
                  : (() => {
                      const nonClientMessage = chatDetail?.messages?.find(
                        (msg: any) => msg.isClientMessage === false
                      );
                      return nonClientMessage?.user?.name
                        ? initials(nonClientMessage.user.name)
                        : "AI";
                    })();

                return (
                  <li
                    key={key}
                    className={cn(
                      "relative flex chat-message group/item",
                      !isClient && "justify-end"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute -top-4 text-[10px] text-muted-foreground",
                        isClient ? "left-12" : "right-12"
                      )}
                    >
                      {item.activityCreatedOnTime
                        ? formatDateTime(item.activityCreatedOnTime)
                        : formatDateTime(item?.creationTime)}
                    </span>

                    <div
                      className={cn(
                        "flex max-w-[85%] gap-3 sm:max-w-[75%]",
                        !isClient && "flex-row-reverse"
                      )}
                    >
                      <Avatar className="h-9 w-9 self-end">
                        <AvatarFallback className="text-xs">
                          {senderInitials}
                        </AvatarFallback>
                      </Avatar>

                      <div className="relative min-w-0 flex-1">
                        {!isClient && (
                          <span className="absolute -bottom-5 right-0 text-[10px] text-muted-foreground">
                            Sender:{" "}
                            {item.activityCreatedBy ??
                              item?.user?.userName ??
                              "Alle"}
                          </span>
                        )}

                        <div
                          className={cn(
                            "relative break-words rounded-2xl px-4 py-3 shadow-soft",
                            bubbleStyle(item),
                            isClient ? "rounded-bl-sm" : "rounded-br-sm"
                          )}
                        >
                          {item.sendType === SendType.Email ||
                          item.sendType === SendType.EmailMarketing ||
                          item.sendType === SendType.ActivityNote ||
                          item.sendType === SendType.RequestReview ||
                          item.sendType === SendType.SystemNote ? (
                            <div
                              className="display-content customSpanTag overflow-auto text-sm scrollbar-hide"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.content),
                              }}
                            />
                          ) : (
                            <div className="display-content customSpanTag overflow-auto whitespace-pre-line text-sm scrollbar-hide">
                              {item.content}
                            </div>
                          )}

                          {!isClient && item?.statusSMS === "Invalid" && (
                            <span
                              className="absolute -left-2 -top-2 rounded-full bg-card p-0.5 text-destructive"
                              data-tooltip-id="message-info-tooltip"
                              data-tooltip-content="Invalid phone number"
                            >
                              <Info className="h-3 w-3" />
                              <Tooltip id="message-info-tooltip" place="top" />
                            </span>
                          )}

                          <span
                            className={cn(
                              "absolute -bottom-1 inline-flex h-4 items-center gap-1 rounded-full bg-card/95 px-1.5 text-muted-foreground shadow-soft",
                              isClient ? "left-2" : "right-2"
                            )}
                          >
                            {sendTypeLabel(item?.sendType)}
                          </span>
                        </div>

                        {!isClient && (
                          <span className="absolute -bottom-5 left-0 text-[10px] text-muted-foreground">
                            To:{" "}
                            {(() => {
                              const text = item?.sendTo
                                ? item.sendTo
                                : item?.user?.name;
                              return text && text.length > 20
                                ? text.substring(0, 20) + "..."
                                : text;
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </SimpleBar>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-border/60 bg-card px-4 py-3">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Input
              id="inputText"
              type="text"
              placeholder="Type your message here..."
              value={curMessage}
              onKeyDown={onKeyPress}
              onChange={(e) => setCurMessage(e.target.value)}
              className="h-11"
            />
          </div>
          <div className="shrink-0">
            <SplitButton
              contact={contactDetail}
              buttonText={buttonText}
              isSendingMessage={isSendingMessage}
              onMainAction={addMessage}
              onOptionSelect={(option: SendType) => {
                setSendType(option);
                setButtonText(option);
              }}
            />
          </div>
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        title="Delete Conversation"
        content="Are you sure you want to delete this conversation? This action cannot be undone."
        cancelText="Cancel"
        deleteText="Delete"
      />

      <ContactInfoPanel
        show={showContactPanel}
        onHide={() => setShowContactPanel(false)}
        chatDetail={chatDetail}
        contactDetail={contactDetail}
        contactId={contactId}
      />
    </div>
  );
};

export default ChatMessages;
